import axios from 'axios'
import React, { useEffect, useCallback, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsMobile } from '../../hooks'

const NICE_CERT_STORAGE_KEY = 'niceCertificationResult'
const NICE_CERT_START_KEY = 'niceCertificationStart'

const CertifyComplete = () => {
	console.log('들어옴')
	const location = useLocation()
	const navigate = useNavigate()
	const isPopup = !!window.opener
	const isMobile = useIsMobile()
	const [isProcessing, setIsProcessing] = useState(false)
	const checkCountRef = useRef(0)

	const processCertificationResult = useCallback(async () => {
		// 모든 URL 파라미터 확인 (다양한 형태 대응)
		const urlParams = new URLSearchParams(location.search)

		// 원래 페이지로 돌아갈 경로 확인
		const redirectPath =
			urlParams.get('redirect') ||
			(() => {
				const certStart = localStorage.getItem(NICE_CERT_START_KEY)
				if (certStart) {
					try {
						const startInfo = JSON.parse(certStart)
						return startInfo.originalPath
					} catch (e) {
						console.error('인증 시작 정보 파싱 실패:', e)
					}
				}
				return null
			})()

		console.log('리다이렉트 경로:', redirectPath)

		// 백엔드에서 리다이렉트된 결과 확인 (모바일에서 백엔드 처리 후 리다이렉트된 경우)
		// 이 경우는 SelfVerification에서 처리하므로 여기서는 무시
		if (urlParams.get('cert') === 'complete') {
			console.log('백엔드에서 리다이렉트된 결과 - SelfVerification에서 처리됨')
			// SelfVerification의 processUrlParams에서 처리하므로 여기서는 리다이렉트만
			return
		}

		// 다양한 파라미터 이름 시도 (대소문자, 언더스코어 등) - 웹 환경
		let tokenVersionId =
			urlParams.get('token_version_id') ||
			urlParams.get('tokenVersionId') ||
			urlParams.get('token-version-id') ||
			urlParams.get('TokenVersionId')
		let encData =
			urlParams.get('enc_data') ||
			urlParams.get('encData') ||
			urlParams.get('enc-data') ||
			urlParams.get('EncData')
		let integrityValue =
			urlParams.get('integrity_value') ||
			urlParams.get('integrityValue') ||
			urlParams.get('integrity-value') ||
			urlParams.get('IntegrityValue')

		// hash fragment도 확인 (일부 모바일 브라우저에서 사용)
		if (!tokenVersionId || !encData) {
			const hash = window.location.hash
			if (hash) {
				const hashParams = new URLSearchParams(hash.substring(1))
				tokenVersionId =
					tokenVersionId ||
					hashParams.get('token_version_id') ||
					hashParams.get('tokenVersionId')
				encData = encData || hashParams.get('enc_data') || hashParams.get('encData')
				integrityValue =
					integrityValue ||
					hashParams.get('integrity_value') ||
					hashParams.get('integrityValue')
			}
		}

		const targetOrigin = window.location.origin

		let finalResultForParent = {
			type: 'niceCertificationFinalResult', // 메시지 타입
			status: 'failed', // 초기 상태: 실패
			message: '본인인증 처리 실패: 알 수 없는 오류',
			userInfo: null, // 사용자 정보
		}

		// 모든 URL 정보 로깅
		console.log('=== 인증 완료 페이지 진입 ===')
		console.log('전체 URL:', window.location.href)
		console.log('location.search:', location.search)
		console.log('location.hash:', window.location.hash)
		console.log('모든 쿼리 파라미터:', Object.fromEntries(urlParams.entries()))
		console.log('추출된 파라미터:', { encData, tokenVersionId, integrityValue })

		// 필수 파라미터 누락 여부 검증
		if (!tokenVersionId || !encData || !integrityValue) {
			console.warn('필수 본인인증 결과 파라미터가 URL에 누락되었습니다.')

			// 모바일 환경: URL 파라미터가 없으면 즉시 에러 처리 (polling 불필요)
			// 모바일은 백엔드에서 처리하므로 여기 도달하면 안 됨
			if (isMobile) {
				finalResultForParent.message = '본인인증 결과 파라미터 누락. 다시 시도해주세요.'
				localStorage.removeItem(NICE_CERT_START_KEY)

				// 원래 페이지로 리다이렉트
				if (redirectPath) {
					console.log('모바일: 파라미터 누락 - 원래 페이지로 리다이렉트:', redirectPath)
					window.location.href = redirectPath
				} else {
					window.history.back()
				}
				return
			}

			// 웹 환경: 인증 시작 정보가 있는지 확인 (새 창으로 이동한 경우)
			const certStart = localStorage.getItem(NICE_CERT_START_KEY)
			if (certStart) {
				const startInfo = JSON.parse(certStart)
				const elapsed = Date.now() - startInfo.timestamp
				console.log('웹: 인증 시작 정보 발견, 경과 시간:', elapsed, 'ms')

				// 웹에서 새 창으로 이동한 경우, 주기적으로 확인
				if (elapsed < 5 * 60 * 1000 && elapsed > 500) {
					console.log('웹: 파라미터 대기 중... 주기적으로 재확인')
					setIsProcessing(true) // 주기적 확인 시작
					return // 일단 대기, useEffect에서 주기적으로 확인
				} else if (elapsed < 500) {
					console.log('웹: 인증 시작 직후, 잠시 대기...')
					setIsProcessing(true)
					return
				} else {
					// 타임아웃
					finalResultForParent.message =
						'본인인증 시간이 초과되었습니다. 다시 시도해주세요.'
					localStorage.removeItem(NICE_CERT_START_KEY)
				}
			} else {
				finalResultForParent.message = '본인인증 결과 파라미터 누락. 다시 시도해주세요.'
			}

			// 즉시 처리해야 하는 경우만 실행
			if (finalResultForParent.message !== '') {
				// 팝업인 경우 postMessage, 아닌 경우 storage 사용
				if (isPopup && window.opener) {
					window.opener.postMessage(finalResultForParent, targetOrigin)
					window.close()
				} else {
					// 새 창으로 이동한 경우 sessionStorage와 localStorage 모두에 저장
					sessionStorage.setItem(
						NICE_CERT_STORAGE_KEY,
						JSON.stringify(finalResultForParent),
					)
					localStorage.setItem(
						NICE_CERT_STORAGE_KEY,
						JSON.stringify(finalResultForParent),
					)
					// 원래 페이지로 직접 리다이렉트 (history.back 대신)
					if (redirectPath) {
						console.log('웹: 원래 페이지로 리다이렉트:', redirectPath)
						window.location.href = redirectPath
					} else {
						// 경로가 없으면 이전 페이지로 이동 (fallback)
						window.history.back()
					}
				}
			}
			return
		}

		// 파라미터가 있으면 처리 시작
		setIsProcessing(false)
		const apiUrl = `${process.env.REACT_APP_API_ENDPOINT}/users/niceapi/decrypt`

		// const apiUrl = 'http://15.164.255.185:3001/api/users/niceapi/decrypt'

		console.log('백엔드 API 호출 준비:', { tokenVersionId, encData })

		try {
			const response = await axios.post(apiUrl, {
				encData: encData,
				tokenVersionId: tokenVersionId,
				// integrityValue: integrityValue,
			})

			console.log('백엔드 API 응답 수신:', response.data)

			if (response.data && response.data.name && response.data.phone) {
				finalResultForParent.status = 'success'
				finalResultForParent.message = '본인인증이 성공적으로 완료되었습니다.'
				// userInfo 객체를 직접 생성하여 이름과 전화번호를 담습니다.
				finalResultForParent.userInfo = {
					name: response.data.name,
					phoneNumber: response.data.phone,
				}
			} else {
				// 백엔드 응답은 받았지만 예상치 못한 구조인 경우
				finalResultForParent.message =
					response.data.message || '백엔드 본인인증 처리 실패: 응답 데이터 구조 불일치'
				console.error('백엔드에서 예상치 못한 응답 구조:', response.data)
			}
		} catch (error) {
			console.error('본인인증 최종 결과 처리 중 치명적인 오류:', error)
			if (error.response) {
				console.error('응답 데이터:', error.response.data)
				finalResultForParent.message = `백엔드 요청 실패: ${error.response.data.message || error.message}`
			} else if (error.request) {
				console.error('응답을 받지 못했습니다:', error.request)
				finalResultForParent.message = '네트워크 오류: 서버에 연결할 수 없습니다.'
			} else {
				console.error('요청 설정 오류:', error.message)
				finalResultForParent.message = `클라이언트 요청 설정 오류: ${error.message}`
			}
		} finally {
			// 인증 시작 정보 제거
			localStorage.removeItem(NICE_CERT_START_KEY)

			// 팝업인 경우 postMessage 사용
			if (isPopup && window.opener) {
				window.opener.postMessage(finalResultForParent, targetOrigin)
				window.close()
			} else {
				// 모바일 환경: 이미 CertifyComplete에서 처리했으므로 원래 페이지로 리다이렉트 (URL 파라미터 불필요)
				if (isMobile) {
					console.log('모바일: 원래 페이지로 리다이렉트:', redirectPath)
					if (redirectPath) {
						window.location.href = redirectPath
					} else {
						window.history.back()
					}
				} else {
					// 웹 환경: 새 창으로 이동한 경우 sessionStorage와 localStorage 모두에 저장
					console.log('웹: 새 창 모드 - storage에 결과 저장', finalResultForParent)
					sessionStorage.setItem(
						NICE_CERT_STORAGE_KEY,
						JSON.stringify(finalResultForParent),
					)
					localStorage.setItem(
						NICE_CERT_STORAGE_KEY,
						JSON.stringify(finalResultForParent),
					)
					// 원래 페이지로 직접 리다이렉트 (history.back 대신)
					if (redirectPath) {
						console.log('웹: 원래 페이지로 리다이렉트:', redirectPath)
						window.location.href = redirectPath
					} else {
						// 경로가 없으면 이전 페이지로 이동 (fallback)
						window.history.back()
					}
				}
			}
		}
	}, [location.search, isPopup, isMobile])

	useEffect(() => {
		processCertificationResult()
	}, [processCertificationResult])

	// URL 파라미터가 없을 때 주기적으로 재확인 (웹 환경에서만, 새 창으로 이동한 경우)
	useEffect(() => {
		// 모바일 환경: polling 불필요 (URL 파라미터가 바로 있음)
		if (isMobile || !isProcessing) return

		const certStart = localStorage.getItem(NICE_CERT_START_KEY)
		if (!certStart) {
			setIsProcessing(false)
			return
		}

		const startInfo = JSON.parse(certStart)
		const elapsed = Date.now() - startInfo.timestamp

		// 타임아웃 체크 (5분)
		if (elapsed > 5 * 60 * 1000) {
			console.log('웹: 파라미터 대기 타임아웃', elapsed, 'ms')
			setIsProcessing(false)

			const finalResultForParent = {
				type: 'niceCertificationFinalResult',
				status: 'failed',
				message: '본인인증 시간이 초과되었습니다. 다시 시도해주세요.',
				userInfo: null,
			}

			localStorage.removeItem(NICE_CERT_START_KEY)

			// redirectPath 가져오기
			const certStartForRedirect = localStorage.getItem(NICE_CERT_START_KEY)
			let redirectPathForTimeout = null
			if (certStartForRedirect) {
				try {
					const startInfo = JSON.parse(certStartForRedirect)
					redirectPathForTimeout = startInfo.originalPath
				} catch (e) {
					console.error('인증 시작 정보 파싱 실패:', e)
				}
			}

			if (isPopup && window.opener) {
				window.opener.postMessage(finalResultForParent, window.location.origin)
				window.close()
			} else {
				sessionStorage.setItem(NICE_CERT_STORAGE_KEY, JSON.stringify(finalResultForParent))
				localStorage.setItem(NICE_CERT_STORAGE_KEY, JSON.stringify(finalResultForParent))
				// 원래 페이지로 직접 리다이렉트
				if (redirectPathForTimeout) {
					console.log('웹: 타임아웃 - 원래 페이지로 리다이렉트:', redirectPathForTimeout)
					window.location.href = redirectPathForTimeout
				} else {
					window.history.back()
				}
			}
			return
		}

		// 2초마다 URL 파라미터 확인 (웹 환경에서만)
		const interval = setInterval(() => {
			checkCountRef.current += 1
			console.log(`웹: 파라미터 확인 시도 ${checkCountRef.current}회`)

			// URL이 변경되었는지 확인
			const currentSearch = window.location.search
			if (currentSearch && currentSearch !== location.search) {
				console.log('웹: URL 파라미터 발견! 재처리')
				clearInterval(interval)
				setIsProcessing(false)
				window.location.reload()
			}
		}, 2000)

		return () => {
			clearInterval(interval)
		}
	}, [isProcessing, location.search, isPopup, isMobile])

	return (
		<div>
			<p>본인인증 결과 확인 중...</p>
		</div>
	)
}

export default CertifyComplete
